import { makeStyles } from '@material-ui/core/styles'
import {
  CARD_BORDER,
  MUTED_TEXT,
} from 'pages/schedule/SelectLessonTime/redesign/styles'
import {
  CARD_SHADOW,
  SOFT_PANEL_BACKGROUND,
} from 'pages/things-to-do/events/register/registration-with-payment-intents/redesign/styles'
import {
  PGA_COACH_ERROR_BG,
  PGA_COACH_ERROR_TEXT,
} from 'lib/styles/pga-coach-pill-colors'

// Page chrome (page background, container, grid, mobile top card, summary card
// and the sticky bottom bar) comes from the step 1 redesign sheet so both steps
// stay visually identical. This sheet only adds what step 2 needs: the "Your
// time" card, segmented controls, form fields and the student picker. Colors,
// fonts and buttons come from the `offeringsRefresh` theme.
const FIELD_BORDER = '#D9DCE1'

const card = theme => ({
  backgroundColor: theme.palette.common.white,
  border: `1px solid ${CARD_BORDER}`,
  borderRadius: 24,
  boxShadow: CARD_SHADOW,
  [theme.breakpoints.down('xs')]: {
    borderRadius: 20,
  },
})

const focusRing = theme => ({
  outline: `2px solid ${theme.palette.primary.lightest}`,
  outlineOffset: 2,
})

const useStyles = makeStyles(theme => ({
  srOnly: {
    position: 'absolute',
    width: 1,
    height: 1,
    margin: -1,
    padding: 0,
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    border: 0,
  },
  stack: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2.5),
  },

  // "Your time" card
  yourTime: {
    ...card(theme),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    padding: theme.spacing(1.75, 2.5),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(1.5, 2),
    },
  },
  yourTimeLabel: {
    display: 'block',
    margin: 0,
    color: MUTED_TEXT,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.12em',
    lineHeight: '16px',
    textTransform: 'uppercase',
  },
  yourTimeValue: {
    margin: theme.spacing(0.25, 0, 0),
    fontSize: 16,
    fontWeight: 600,
    lineHeight: 1.4,
  },

  // Form card
  formCard: {
    ...card(theme),
    padding: theme.spacing(3.5, 3.5, 3),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(2.5),
    },
  },
  heading: {
    margin: 0,
    fontSize: 24,
    lineHeight: 1.25,
  },
  // "Have an account?" prompt: the shared neutral TipWell, restyled like the
  // sign-in prompt on the Virtual Coaching checkout (`signInTip`, `tipBody`,
  // `learnMore` in pages/virtual-coaching-student/styles.js).
  signInTip: {
    margin: theme.spacing(1, 0, 2.5),
    '&& > *': {
      borderColor: CARD_BORDER,
      borderRadius: 16,
      backgroundColor: SOFT_PANEL_BACKGROUND,
    },
  },
  signInTipBody: {
    display: 'block',
    fontSize: '0.9375rem',
    lineHeight: 1.6,
    color: theme.palette.text.primary,
  },
  signInLink: {
    padding: 0,
    border: 0,
    background: 'none',
    color: theme.palette.primary.main,
    cursor: 'pointer',
    font: 'inherit',
    fontWeight: 700,
    textDecoration: 'underline',
    '&:focus': { outline: 'none' },
    '&:focus-visible': {
      ...focusRing(theme),
      borderRadius: 4,
    },
  },
  subtitle: {
    margin: theme.spacing(0.75, 0, 2.5),
    color: MUTED_TEXT,
    fontSize: 14,
    lineHeight: 1.5,
  },
  linkButton: {
    display: 'inline',
    padding: 0,
    border: 0,
    background: 'none',
    color: theme.palette.primary.lightest,
    cursor: 'pointer',
    font: 'inherit',
    fontWeight: 600,
    textDecoration: 'none',
    '&:hover': { textDecoration: 'underline' },
    '&:focus': { outline: 'none' },
    '&:focus-visible': {
      ...focusRing(theme),
      borderRadius: 4,
    },
  },
  sectionGap: {
    marginTop: theme.spacing(2.5),
  },
  subheading: {
    margin: theme.spacing(3, 0, 1.5),
    fontSize: 14,
    fontWeight: 700,
    lineHeight: 1.4,
  },
  ageRow: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(2.5),
  },
  ageLabel: {
    margin: 0,
    fontSize: 14,
    fontWeight: 600,
    lineHeight: 1.4,
  },

  // Field grid. The group event sheet (`fieldGrid`) puts labels above inputs;
  // these rules round the inputs and tune weights to the booking design.
  fields: {
    marginTop: theme.spacing(2.5),
    '& label, & label[data-shrink]': {
      fontWeight: 600,
    },
    '& [class*="MuiOutlinedInput-root"]': {
      borderRadius: 12,
      backgroundColor: theme.palette.common.white,
    },
    '& [class*="MuiOutlinedInput-notchedOutline"]': {
      borderColor: FIELD_BORDER,
      borderRadius: 12,
    },
    '& [class*="MuiOutlinedInput-root"]:hover [class*="MuiOutlinedInput-notchedOutline"]': {
      borderColor: theme.palette.primary.main,
    },
    '& [class*="MuiOutlinedInput-root"][class*="Mui-focused"] [class*="MuiOutlinedInput-notchedOutline"]': {
      borderColor: theme.palette.primary.main,
      borderWidth: 2,
    },
    '& [class*="MuiOutlinedInput-root"][class*="Mui-error"] [class*="MuiOutlinedInput-notchedOutline"]': {
      borderColor: theme.palette.error.main,
    },
    '& input': {
      height: 'auto',
      padding: theme.spacing(1.75, 1.75),
      fontSize: 15,
    },
    // The Autocomplete behind the email field ships its own outlined padding
    // (9px on the root plus 9.5px 4px on the input), which made it taller than
    // the plain inputs. Flatten it to the same box as every other input; the
    // extra attribute selectors outrank the Autocomplete sheet.
    '& [class*="MuiAutocomplete-inputRoot"][class*="MuiOutlinedInput-root"]': {
      padding: 0,
    },
    '& [class*="MuiAutocomplete-inputRoot"][class*="MuiOutlinedInput-root"] input[class*="MuiAutocomplete-input"]': {
      padding: theme.spacing(1.75, 1.75),
    },
    '& input::placeholder': {
      color: MUTED_TEXT,
      opacity: 1,
    },
    '& [class*="MuiFormHelperText-root"]': {
      marginLeft: 2,
      marginRight: 2,
      color: MUTED_TEXT,
      fontSize: 12,
    },
    '& [class*="MuiFormHelperText-root"][class*="Mui-error"]': {
      color: theme.palette.error.main,
    },
  },

  // Segmented control (native radios inside labels, so arrow keys, focus and
  // checked state come from the browser).
  segmented: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 2,
    padding: 3,
    borderRadius: 999,
    backgroundColor: '#F0F1F4',
  },
  segment: {
    position: 'relative',
    display: 'inline-flex',
    cursor: 'pointer',
  },
  segmentInput: {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    margin: 0,
    opacity: 0,
    cursor: 'pointer',
  },
  segmentFace: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 32,
    padding: theme.spacing(0, 2),
    borderRadius: 999,
    color: MUTED_TEXT,
    fontFamily: theme.typography.fontFamily,
    fontSize: 14,
    fontWeight: 600,
    whiteSpace: 'nowrap',
    transition: 'background-color 120ms, color 120ms',
    '$segmentInput:hover + &': { color: theme.palette.text.primary },
    '$segmentInput:focus-visible + &': focusRing(theme),
    '$segmentInput:checked + &': {
      backgroundColor: theme.palette.common.white,
      boxShadow: '0 1px 3px rgba(0, 10, 22, 0.12)',
      color: theme.palette.text.primary,
      fontWeight: 700,
    },
    [theme.breakpoints.down('xs')]: {
      minHeight: 40,
    },
  },

  // Student picker
  searchField: {
    marginBottom: theme.spacing(1.5),
  },
  pickerList: {
    maxHeight: 320,
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    margin: 0,
    padding: theme.spacing(0.5),
  },
  pickerOption: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.25, 1.5),
    borderRadius: 14,
    border: `1px solid ${FIELD_BORDER}`,
    backgroundColor: theme.palette.common.white,
    cursor: 'pointer',
    '&:hover': { borderColor: theme.palette.primary.main },
    '&:focus-within': focusRing(theme),
  },
  pickerOptionSelected: {
    borderColor: theme.palette.primary.main,
    backgroundColor: SOFT_PANEL_BACKGROUND,
    boxShadow: `inset 0 0 0 1px ${theme.palette.primary.main}`,
  },
  pickerInput: {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    margin: 0,
    opacity: 0,
    cursor: 'pointer',
  },
  pickerAvatar: {
    flexShrink: 0,
    width: 36,
    height: 36,
    backgroundColor: theme.palette.grey.light,
    color: theme.palette.common.white,
    fontSize: 14,
  },
  pickerName: {
    fontSize: 15,
    fontWeight: 600,
    lineHeight: 1.3,
  },
  pickerCaption: {
    color: MUTED_TEXT,
    fontSize: 12,
    lineHeight: 1.4,
  },
  pickerCheck: {
    marginLeft: 'auto',
    color: theme.palette.primary.main,
    fontSize: 20,
  },
  emptyNote: {
    margin: 0,
    color: MUTED_TEXT,
    fontSize: 14,
    lineHeight: 1.5,
  },
  // The theme's outlined button is 72px wide on each side; keep it compact.
  outlinedButton: {
    '&&': {
      padding: theme.spacing(0, 3),
    },
    '&&, && [class*="MuiButton-label"]': { textTransform: 'none' },
  },
  addStudentButton: {
    marginTop: theme.spacing(2),
    alignSelf: 'flex-start',
  },
  signedIn: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(2.5),
    padding: theme.spacing(1.5, 2),
    borderRadius: 14,
    border: `1px solid ${CARD_BORDER}`,
    backgroundColor: SOFT_PANEL_BACKGROUND,
  },
  signedInName: {
    margin: 0,
    fontSize: 15,
    fontWeight: 700,
    lineHeight: 1.3,
  },
  signedInEmail: {
    margin: 0,
    color: MUTED_TEXT,
    fontSize: 13,
    lineHeight: 1.4,
    overflowWrap: 'anywhere',
  },

  // Errors
  inlineError: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
    padding: theme.spacing(1, 1.5),
    borderRadius: 12,
    backgroundColor: PGA_COACH_ERROR_BG,
    color: PGA_COACH_ERROR_TEXT,
    fontSize: 14,
    lineHeight: 1.4,
    '& svg': { flexShrink: 0, fontSize: 18 },
  },

  // Actions
  // The theme capitalizes every button label ("Continue To Payment") on both
  // the button and its inner label span; step 2 copy is sentence case, so
  // these buttons render as typed.
  // `&&` outranks the button sheet regardless of injection order.
  submitButton: {
    '&&, && [class*="MuiButton-label"]': { textTransform: 'none' },
  },
  actions: {
    marginTop: theme.spacing(3),
  },
  actionsAccount: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(3),
  },
  // Summary card rows
  factStrong: {
    color: theme.palette.text.primary,
    fontWeight: 600,
  },
  summaryCallout: {
    marginTop: theme.spacing(2),
  },
  mobileCallout: {
    marginTop: theme.spacing(2),
  },
}))

export default useStyles
